/* eslint-disable prefer-spread */
import React, { useEffect, useRef } from 'react'
import {
	Link,
	Route,
	Switch,
	useLocation as useWouterLocation,
	useRoute,
} from 'wouter'
import { Helmet } from 'react-helmet'
import { get } from 'lodash'
import ReactGA from 'react-ga'
import { Rnd } from 'react-rnd'
import Markdown from 'markdown-to-jsx'
import { useMediaQuery } from 'react-responsive'
import ReactPlayer from 'react-player'
import { useImmer } from 'use-immer'
import screenfull from 'screenfull'

// Components
import Contact from 'containers/Contact'
import Four from 'containers/Four'
import Header from 'components/Header'
import Home from 'containers/Home'
import Release from 'components/Release'
import Timeline from 'components/Timeline'
import Videos from 'containers/Videos'

// Content
import videoData from 'content/videos'

import { ThemeContext } from '../../context'

import style from './index.module.css'

const HEADER_HEIGHT = 51
const FOOTER_HEIGHT = 59
const VIDEO_WIDTH = 560
const VIDEO_HEIGHT = 315
const Z_INDEX = 100

const getRandomNumber = (min, max, offset) => Math.random() * ((max - min) + min - offset)

/* eslint-disable-next-line react/prop-types */
const SVG = ({ children }) => (
	<svg
		fill="none"
		height="24"
		viewBox="0 0 24 24"
		width="24"
		xmlns="http://www.w3.org/2000/svg"
	>
		{ children }
	</svg>
)

// Icons
const Play = () => (
	<SVG>
		<path d="M8.72154 2.54687C6.73333 1.15374 4 2.57604 4 5.00375V18.9965C4 21.4243 6.73336 22.8466 8.72156 21.4534L18.7297 14.4406C20.4229 13.2541 20.4229 10.746 18.7297 9.55955L8.72154 2.54687Z" fill="currentColor" />
	</SVG>
)

const Pause = () => (
	<SVG>
		<>
			<path d="M3.21799 4.09202C3 4.51984 3 5.0799 3 6.2V17.8C3 18.9201 3 19.4802 3.21799 19.908C3.40973 20.2843 3.71569 20.5903 4.09202 20.782C4.51984 21 5.0799 21 6.2 21H6.8C7.9201 21 8.48016 21 8.90798 20.782C9.28431 20.5903 9.59027 20.2843 9.78201 19.908C10 19.4802 10 18.9201 10 17.8V6.2C10 5.0799 10 4.51984 9.78201 4.09202C9.59027 3.71569 9.28431 3.40973 8.90798 3.21799C8.48016 3 7.9201 3 6.8 3H6.2C5.0799 3 4.51984 3 4.09202 3.21799C3.71569 3.40973 3.40973 3.71569 3.21799 4.09202Z" fill="currentColor" />
			<path d="M14.218 4.09202C14 4.51984 14 5.0799 14 6.2V17.8C14 18.9201 14 19.4802 14.218 19.908C14.4097 20.2843 14.7157 20.5903 15.092 20.782C15.5198 21 16.0799 21 17.2 21H17.8C18.9201 21 19.4802 21 19.908 20.782C20.2843 20.5903 20.5903 20.2843 20.782 19.908C21 19.4802 21 18.9201 21 17.8V6.2C21 5.0799 21 4.51984 20.782 4.09202C20.5903 3.71569 20.2843 3.40973 19.908 3.21799C19.4802 3 18.9201 3 17.8 3H17.2C16.0799 3 15.5198 3 15.092 3.21799C14.7157 3.40973 14.4097 3.71569 14.218 4.09202Z" fill="currentColor" />
		</>
	</SVG>
)

const Eject = () => (
	<SVG>
		<>
			<path d="M4 21C3.44772 21 3 20.5523 3 20C3 19.4477 3.44772 19 4 19L20 19C20.5523 19 21 19.4477 21 20C21 20.5523 20.5523 21 20 21L4 21Z" fill="currentColor" />
			<path d="M3.36297 13.8574C2.43993 15.1834 3.3888 17 5.00445 17L18.9953 17C20.611 17 21.5598 15.1834 20.6368 13.8574L13.6989 3.89033C12.8753 2.70714 11.1246 2.70713 10.301 3.89032L3.36297 13.8574Z" fill="currentColor" />
		</>
	</SVG>
)

const Fullscreen = () => (
	<SVG>
		<path
			clipRule="evenodd"
			d="M3 9.4C3 7.15979 3 6.03969 3.43597 5.18404C3.81947 4.43139 4.43139 3.81947 5.18404 3.43597C6.03969 3 7.15979 3 9.4 3H14.6C16.8402 3 17.9603 3 18.816 3.43597C19.5686 3.81947 20.1805 4.43139 20.564 5.18404C21 6.03969 21 7.15979 21 9.4V14.6C21 16.8402 21 17.9603 20.564 18.816C20.1805 19.5686 19.5686 20.1805 18.816 20.564C17.9603 21 16.8402 21 14.6 21H9.4C7.15979 21 6.03969 21 5.18404 20.564C4.43139 20.1805 3.81947 19.5686 3.43597 18.816C3 17.9603 3 16.8402 3 14.6V9.4ZM6 14C6.55228 14 7 14.4477 7 15C7 15.4796 7.00054 15.7893 7.01671 16.0262C7.03227 16.2542 7.05888 16.3411 7.07612 16.3827C7.17761 16.6277 7.37229 16.8224 7.61732 16.9239C7.65893 16.9411 7.74576 16.9677 7.97376 16.9833C8.21074 16.9995 8.52038 17 9 17C9.55228 17 10 17.4477 10 18C10 18.5523 9.55228 19 9 19H8.96801C8.52936 19 8.15087 19 7.83762 18.9787C7.50779 18.9561 7.17788 18.9066 6.85195 18.7716C6.11687 18.4672 5.53284 17.8831 5.22836 17.1481C5.09336 16.8221 5.04385 16.4922 5.02135 16.1624C4.99998 15.8491 4.99999 15.4706 5 15.032L5 15C5 14.4477 5.44772 14 6 14ZM17 15C17 14.4477 17.4477 14 18 14C18.5523 14 19 14.4477 19 15V15.032C19 15.4706 19 15.8491 18.9787 16.1624C18.9561 16.4922 18.9066 16.8221 18.7716 17.1481C18.4672 17.8831 17.8831 18.4672 17.1481 18.7716C16.8221 18.9066 16.4922 18.9561 16.1624 18.9787C15.8491 19 15.4706 19 15.032 19H15C14.4477 19 14 18.5523 14 18C14 17.4477 14.4477 17 15 17C15.4796 17 15.7893 16.9995 16.0262 16.9833C16.2542 16.9677 16.3411 16.9411 16.3827 16.9239C16.6277 16.8224 16.8224 16.6277 16.9239 16.3827C16.9411 16.3411 16.9677 16.2542 16.9833 16.0262C16.9995 15.7893 17 15.4796 17 15ZM6 10C6.55228 10 7 9.55228 7 9C7 8.52038 7.00054 8.21074 7.01671 7.97376C7.03227 7.74576 7.05888 7.65893 7.07612 7.61732C7.17761 7.37229 7.37229 7.17761 7.61732 7.07612C7.65893 7.05888 7.74576 7.03227 7.97376 7.01671C8.21074 7.00054 8.52038 7 9 7C9.55228 7 10 6.55228 10 6C10 5.44772 9.55228 5 9 5L8.96801 5C8.52936 4.99999 8.15087 4.99998 7.83762 5.02135C7.50779 5.04385 7.17788 5.09336 6.85195 5.22836C6.11687 5.53284 5.53284 6.11687 5.22836 6.85195C5.09336 7.17788 5.04385 7.50779 5.02135 7.83762C4.99998 8.15087 4.99999 8.52936 5 8.968L5 9C5 9.55228 5.44772 10 6 10ZM17 9C17 9.55228 17.4477 10 18 10C18.5523 10 19 9.55228 19 9V8.96802C19 8.52937 19 8.15088 18.9787 7.83762C18.9561 7.50779 18.9066 7.17788 18.7716 6.85195C18.4672 6.11687 17.8831 5.53284 17.1481 5.22836C16.8221 5.09336 16.4922 5.04385 16.1624 5.02135C15.8491 4.99998 15.4706 4.99999 15.032 5L15 5C14.4477 5 14 5.44772 14 6C14 6.55228 14.4477 7 15 7C15.4796 7 15.7893 7.00054 16.0262 7.01671C16.2542 7.03227 16.3411 7.05888 16.3827 7.07612C16.6277 7.17761 16.8224 7.37229 16.9239 7.61732C16.9411 7.65893 16.9677 7.74576 16.9833 7.97376C16.9995 8.21074 17 8.52038 17 9Z"
			fill="currentColor"
			fillRule="evenodd"
		/>
	</SVG>
)

const Previous = () => (
	<SVG>
		<path d="M17.8574 3.36297C19.1834 2.43994 21 3.3888 21 5.00445V18.9951C21 20.6108 19.1834 21.5596 17.8574 20.6366L7.89034 13.6988C6.70713 12.8752 6.70713 11.1245 7.89033 10.3009L17.8574 3.36297Z" fill="currentColor" />
		<path d="M5 4C5 3.44772 4.55228 3 4 3C3.44772 3 3 3.44772 3 4V20C3 20.5523 3.44772 21 4 21C4.55228 21 5 20.5523 5 20V4Z" fill="currentColor" />
	</SVG>
)

const PlayIcon = () => (
	<button
		className={ style.PlayIcon }
		type="button"
	>
		<Play />
	</button>
)

const useLocation = () => {
	const [location, setLocation] = useWouterLocation()
	return [location, setLocation, window.location.search]
}

const CustomHandle = props => (
	<div className={ style.ResizeHandle } { ...props } />
)

const BottomRightHandle = () => (
	<div className={ style.ResizeHandleWrapper }>
		<CustomHandle />
	</div>
)

const pad = string => `0${string}`.slice(-2)

// Format time
const seconds = s => {
	const date = new Date(s * 1000)
	const mm = date.getUTCMinutes()
	const ss = pad(date.getUTCSeconds())
	return `${mm}:${ss}`
}

function App() {
	const isSmall = useMediaQuery({ query: '(min-width: 0px) and (max-width: 850px)' })

	const refs = useRef([])

	const [, params] = useRoute('/release/:release')
	const { dispatch } = React.useContext(ThemeContext)
	const [location] = useLocation()
	const release = get(params, 'release', null)

	// Check for URL search parameters
	const url = new URL(window.location)
	const parms = new URLSearchParams(url.search.slice(1))
	const hasVideos = parms.has('videos')

	// Video State
	const [videos, updateVideos] = useImmer([])
	const [isCountdown, setIsCountdown] = useImmer(true)

	// Bootstrap videos.
	useEffect(() => {
		updateVideos(videoData.map(video => ({
			...video,
			duration: 0,
			isLoaded: false,
			isPlaying: false,
			progress: 0,
			ref: null,
			zIndex: Z_INDEX,
		})))
	}, [updateVideos])

	const handleBumpZIndex = id => {
		updateVideos(draft => {
			const activeVideo = draft.findIndex(video => video.id === id)
			const zIndexes = draft.map(item => item.zIndex)
			const maxZIndex = Math.max.apply(Math, zIndexes)
			draft[activeVideo].zIndex = maxZIndex + 1
		})
	}

	const handleOnClickPreview = id => {
		handleBumpZIndex(id)
		updateVideos(draft => {
			const activeVideo = draft.findIndex(video => video.id === id)
			if (activeVideo > -1) {
				draft[activeVideo].isLoaded = true
				draft[activeVideo].isPlaying = true
			}
		})
	}
	const handleOnProgress = (data, id) => {
		updateVideos(draft => {
			const activeVideo = draft.findIndex(video => video.id === id)
			if (activeVideo > -1) {
				draft[activeVideo].progress = data?.played * 100
			}
		})
	}

	const handlePlay = id => {
		updateVideos(draft => {
			const activeVideo = draft.findIndex(video => video.id === id)
			if (activeVideo > -1) {
				draft[activeVideo].isPlaying = true
			}
		})
	}

	const handlePause = id => {
		updateVideos(draft => {
			const activeVideo = draft.findIndex(video => video.id === id)
			if (activeVideo > -1) {
				draft[activeVideo].isPlaying = false
			}
		})
	}

	const handleOnDragStart = id => {
		handleBumpZIndex(id)
	}

	const handleOnPause = id => {
		updateVideos(draft => {
			const activeVideo = draft.findIndex(video => video.id === id)
			if (activeVideo > -1) {
				draft[activeVideo].isPlaying = false
			}
		})
	}

	const handleOnPlay = id => {
		updateVideos(draft => {
			const activeVideo = draft.findIndex(video => video.id === id)
			if (activeVideo > -1) {
				draft[activeVideo].isPlaying = true
			}
		})
	}

	const handleOnDuration = (id, duration) => {
		updateVideos(draft => {
			const activeVideo = draft.findIndex(video => video.id === id)
			if (activeVideo > -1) {
				draft[activeVideo].duration = duration
			}
		})
	}

	const handleOnEnded = id => {
		updateVideos(draft => {
			const activeVideo = draft.findIndex(video => video.id === id)
			if (activeVideo > -1) {
				draft[activeVideo].isPlaying = false
				draft[activeVideo].progress = 100
			}
		})
	}

	const handleToggleCounterDirection = () => {
		setIsCountdown(!isCountdown)
	}

	const handleFullscreen = id => {
		if (screenfull.isEnabled) {
			screenfull.request(refs.current[id].wrapper)
		}
	}

	const handleStartOver = id => {
		refs.current[id].seekTo(0)
	}

	React.useEffect(
		() => {
			ReactGA.pageview(location)
		},
		[location],
	)

	React.useEffect(
		() => {
			if (params && params.release) {
				dispatch({ type: 'theme', theme: params.release })
			}
		},
		[release], // eslint-disable-line react-hooks/exhaustive-deps
	)

	let favicon = '/favicon.ico'
	if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
		favicon = '/favicon-dark.ico'
	}

	/* eslint-disable-next-line prefer-spread */
	const zIndexes = videos.map(item => item.zIndex)
	const closeButtonZ = zIndexes?.length ? Math.max.apply(Math, zIndexes.filter(Boolean)) + 1 : Z_INDEX

	return (
		<main
			className={ [
				style.App,
				!params || params.release === null
					? style['App--home']
					: null,
				params && params.release
					? params.release
					: null,
			].join(' ') }
		>
			<Helmet title="Home" titleTemplate="%s → Palaxy Tracks, A Compendium">
				<link href={ favicon } rel="icon" />
			</Helmet>
			<Header />
			<Timeline />
			<Switch>
				<Route path="/release/:release">
					{ param => <Release release={ param.release } /> }
				</Route>
				<Route component={ Home } path="/" />
				<Route component={ Videos } path="/videos" />
				<Route component={ Contact } path="/contact" />
				<Route component={ Four } path="/:rest*" />
				<Route component={ Four } path="/404" />
			</Switch>
			{
				hasVideos ? (
					<section className={ style.Videos }>
						<Link
							to={ window.location.pathname }
						>
							<a
								className={ style.CloseVideos }
								href="/"
								style={ {
									zIndex: closeButtonZ,
								} }
							>
								<Eject />
							</a>
						</Link>
						{
							videos?.length && videos.map((video, index) => {
								const videoDetails = videos.find(item => item.id === video.id)
								const {
									duration,
									id,
									isLoaded,
									isPlaying,
									progress,
									zIndex,
								} = videoDetails

								const randomTop = getRandomNumber(0, window.innerHeight, HEADER_HEIGHT + FOOTER_HEIGHT + VIDEO_HEIGHT)
								const randomLeft = getRandomNumber(0, window.innerWidth, VIDEO_WIDTH)

								const counter = isCountdown
									? seconds(duration - (duration * (progress / 100)))
									: seconds(duration * (progress / 100))

								const randX = randomLeft - ((window.innerWidth - VIDEO_WIDTH) / 2)
								const randY = randomTop - ((window.innerHeight - (HEADER_HEIGHT + FOOTER_HEIGHT + VIDEO_HEIGHT)) / 2)

								const x = isSmall
									? (10 * index) + 20
									: (window.innerWidth / 2) - (VIDEO_WIDTH / 2) + randX
								const y = isSmall
									? ((HEADER_HEIGHT * 0.7) * index) + 20
									: (window.innerHeight / 2) - ((HEADER_HEIGHT + FOOTER_HEIGHT + VIDEO_HEIGHT) / 2) + randY

								return (
									<Rnd
										bounds={ `.${style.Videos}` }
										default={ {
											width: isSmall ? VIDEO_WIDTH / 2 : VIDEO_WIDTH,
											x,
											y,
										} }
										dragHandleClassName="handle"
										key={ video.id }
										lockAspectRatio={ VIDEO_WIDTH / VIDEO_HEIGHT }
										lockAspectRatioExtraHeight={ isLoaded ? HEADER_HEIGHT + FOOTER_HEIGHT : HEADER_HEIGHT }
										minWidth={ 560 / 2 }
										onDragStart={ () => handleOnDragStart(id) }
										resizeHandleComponent={ { bottomRight: <BottomRightHandle /> } }
										style={ { zIndex } }
									>
										<article className={ style.Video }>
											<header className={ ['handle', style.VideoHeader].join(' ') }>
												<Markdown options={ { wrapper: 'h1' } }>{ video.title }</Markdown>
											</header>
											<div className={ style.VideoBody }>
												<ReactPlayer
													className="react-player"
													config={ {
														youtube: {
															playerVars: {
																modestbranding: 1,
																rel: 0,
															},
														},
													} }
													height="100%"
													light={ video?.poster || true }
													onClickPreview={ () => handleOnClickPreview(id) }
													onDuration={ dur => handleOnDuration(id, dur) }
													onEnded={ () => handleOnEnded(id) }
													onPause={ () => handleOnPause(id) }
													onPlay={ () => handleOnPlay(id) }
													onProgress={ data => handleOnProgress(data, id) }
													playIcon={ <PlayIcon /> }
													playing={ isPlaying }
													progressInterval={ 200 }
													ref={ player => { refs.current[id] = player } }
													url={ video.src }
													width="100%"
												/>
											</div>
											{
												isLoaded ? (
													<footer className={ style.VideoControls }>
														<button
															arial-label="Start over"
															className={ style.VideoControl }
															onClick={ () => handleStartOver(id) }
															type="button"
														>
															<Previous />
														</button>
														{
															isPlaying ? (
																<button
																	arial-label="Pause Video"
																	className={ [style.VideoControl, style['VideoControl--pause']].join(' ') }
																	onClick={ () => handlePause(id) }
																	type="button"
																>
																	<Pause />
																</button>
															) : (
																<button
																	arial-label="Play Video"
																	className={ [style.VideoControl, style['VideoControl--play']].join(' ') }
																	onClick={ () => handlePlay(id) }
																	type="button"
																>
																	<Play />
																</button>
															)
														}
														<div className={ style.Progress }>
															<div className={ style.ProgressBarInterior }>
																<div
																	className={ style.ProgressBar }
																	style={ { width: `${(isLoaded ? progress : 0)}%` } }
																/>
															</div>
														</div>
														<button
															arial-label="Fullscreen"
															className={ style.VideoControl }
															onClick={ () => handleFullscreen(id) }
															type="button"
														>
															<Fullscreen />
														</button>
														<button
															className={ style.Duration }
															onClick={ handleToggleCounterDirection }
															type="button"
														>
															{ isCountdown ? '-' : '' }{ counter }
														</button>
													</footer>
												) : null
											}
										</article>
									</Rnd>
								)
							})
						}
					</section>
				) : null
			}
		</main>
	)
}

export default App
